import { appearanceHash, c3dPortrait, C3D_CELL, type CharacterAppearance } from "@ghostland/shared";
import { loadPartImage } from "./characterLayers.js";
import { compositeAtlas } from "./c3dPipeline.js";

/**
 * Character portraits: pre-rendered 3D close-ups of the face (one per eye variant),
 * drawn onto a pixelated canvas. Used by the table seats. Cached by appearance hash.
 */

const portraitCache = new Map<string, HTMLCanvasElement>();

/** A fresh copy of a cached canvas. A canvas is a DOM node and can only live in ONE place,
 *  so every caller (HUD, each seat) must get its own element — returning the shared cached
 *  canvas would silently MOVE it out of wherever it currently is. */
function cloneCanvas(src: HTMLCanvasElement): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = src.width;
  c.height = src.height;
  const ctx = c.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(src, 0, 0);
  return c;
}

export async function composePortrait(appearance: CharacterAppearance): Promise<HTMLCanvasElement> {
  const hash = appearanceHash(appearance);
  const cached = portraitCache.get(hash);
  if (cached) return cloneCanvas(cached);

  const img = await loadPartImage(`/assets/${c3dPortrait(appearance.face)}`);
  const canvas = document.createElement("canvas");
  canvas.width = img?.width ?? 64;
  canvas.height = img?.height ?? 64;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  if (img) ctx.drawImage(img, 0, 0);

  portraitCache.set(hash, canvas);
  return cloneCanvas(canvas);
}

/**
 * A dressed head-and-chest "bust" for the HUD profile: composites the FULL appearance
 * (body + clothing + hair) and crops the front idle frame to the upper body, so the
 * worn clothes show. Centred on the sprite's opaque pixels (the art isn't centred in its
 * cell). Falls back to a face portrait if compositing fails. Cached by appearance hash.
 */
const BUST_ASPECT = 0.8; // width / height — matches the HUD portrait box
const bustCache = new Map<string, HTMLCanvasElement>();

export async function composeBust(appearance: CharacterAppearance): Promise<HTMLCanvasElement> {
  const hash = appearanceHash(appearance);
  const cached = bustCache.get(hash);
  if (cached) return cloneCanvas(cached);

  const atlas = await compositeAtlas(appearance);
  if (!atlas) return composePortrait(appearance); // no body sheet — fall back to the face

  // Front-facing idle frame 0 lives in cell (col 0, row 0): source rect (0,0,144,144).
  let minX = C3D_CELL, maxX = -1, minY = C3D_CELL, maxY = -1;
  try {
    const actx = atlas.getContext("2d", { willReadFrequently: true })!;
    const d = actx.getImageData(0, 0, C3D_CELL, C3D_CELL).data;
    for (let y = 0; y < C3D_CELL; y++) {
      for (let x = 0; x < C3D_CELL; x++) {
        if (d[(y * C3D_CELL + x) * 4 + 3] > 16) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
  } catch {
    /* tainted canvas — fall through to a full-cell crop below */
  }
  if (maxX < 0) { minX = 0; maxX = C3D_CELL - 1; minY = 0; maxY = C3D_CELL - 1; }

  const cx = (minX + maxX + 1) / 2;
  const charH = maxY - minY || C3D_CELL;
  const cropH = Math.max(24, Math.min(C3D_CELL, Math.round(charH * 0.6))); // head + chest
  const cropW = Math.min(C3D_CELL, Math.round(cropH * BUST_ASPECT));
  const cropX = Math.max(0, Math.min(C3D_CELL - cropW, Math.round(cx - cropW / 2)));
  const cropY = Math.max(0, Math.min(C3D_CELL - cropH, minY - 4));

  const out = document.createElement("canvas");
  out.width = cropW;
  out.height = cropH;
  const octx = out.getContext("2d")!;
  octx.imageSmoothingEnabled = false;
  octx.drawImage(atlas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

  bustCache.set(hash, out);
  return cloneCanvas(out);
}
